import { Link } from 'react-router-dom';
import { ArrowRight, Layers } from 'lucide-react';
import {
  Heart, ShoppingBag, BarChart3, Truck, FileText, Building2, Activity
} from 'lucide-react';
import { TiltCard } from '@/components/ui/TiltCard';
import { openCalendlyPopup } from '@/lib/calendly';

const products = [
  {
    name: 'Fleet Sync Pro',
    tagline: 'Modern Fleet Operations SaaS',
    Icon: Truck,
    iconColor: 'text-blue-500',
    gradientClass: 'from-blue-600 to-cyan-600',
    productSlug: '/products/fleet-sync-pro/',
    category: 'Logistics & Transport',
    tech: ['React', 'Node.js', 'PostgreSQL', 'Supabase'],
    highlights: [
      'Real-time fleet dashboard & work orders',
      'Fuel tracking with MPG analytics',
      '5-role RBAC, multi-tenant RLS',
    ],
  },
  {
    name: 'Residenta',
    tagline: 'AI-Powered Property Management',
    Icon: Building2,
    iconColor: 'text-emerald-600',
    gradientClass: 'from-emerald-500 to-teal-600',
    productSlug: '/products/residenta/',
    category: 'PropTech',
    tech: ['React', 'Node.js', 'PostgreSQL', 'AI/ML'],
    highlights: [
      'AI maintenance classification & forecasting',
      'Full lease lifecycle automation',
      'Resident self-service portal',
    ],
  },
  {
    name: 'GymApp',
    tagline: 'Multi-Tenant Fitness Management',
    Icon: Activity,
    iconColor: 'text-rose-500',
    gradientClass: 'from-rose-500 to-pink-600',
    productSlug: '/products/gymapp/',
    category: 'FitTech',
    tech: ['React Native', 'Node.js', 'Stripe', 'PostgreSQL'],
    highlights: [
      'Class scheduling & QR check-ins',
      'Automated Stripe billing',
      '4-tier RBAC for multi-location gyms',
    ],
  },
  {
    name: 'Azimuth',
    tagline: 'AI Project Estimation & Proposals',
    Icon: FileText,
    iconColor: 'text-orange-500',
    gradientClass: 'from-orange-500 to-amber-500',
    productSlug: '/products/azimuth/',
    category: 'AgencyTech',
    tech: ['React', 'Node.js', 'Claude AI', 'PostgreSQL'],
    highlights: [
      'AI estimates from raw requirements',
      'One-click PRD, TRD & proposal gen',
      'Built-in CRM + email open tracking',
    ],
  },
  {
    name: 'Skill Gauge',
    tagline: 'AI Talent Assessment Platform',
    Icon: BarChart3,
    iconColor: 'text-violet-500',
    gradientClass: 'from-violet-500 to-indigo-600',
    productSlug: '/products/skill-gauge/',
    category: 'HR Tech',
    tech: ['React', 'Node.js', 'AI/ML', 'PostgreSQL'],
    highlights: [
      'AI-adaptive skills assessments',
      'Proctored testing with fraud prevention',
      'ATS integrations (Workday, Greenhouse)',
    ],
  },
  {
    name: 'Smart Retail',
    tagline: 'Unified Commerce & Retail Intelligence',
    Icon: ShoppingBag,
    iconColor: 'text-emerald-500',
    gradientClass: 'from-emerald-500 to-green-600',
    productSlug: '/products/smart-retail-engine/',
    category: 'RetailTech',
    tech: ['React', 'Node.js', 'AI/ML', 'Shopify API'],
    highlights: [
      'Omnichannel POS & inventory management',
      'AI demand forecasting & replenishment',
      'eCommerce integrations (Shopify, WooCommerce)',
    ],
  },
  {
    name: 'Ortem Health',
    tagline: 'AI-Powered Digital Health Platform',
    Icon: Heart,
    iconColor: 'text-rose-500',
    gradientClass: 'from-rose-500 to-red-600',
    productSlug: null,
    externalUrl: 'https://health.ortemtech.com/',
    category: 'HealthTech',
    tech: ['React', 'Node.js', 'AI/ML', 'FHIR/HL7'],
    highlights: [
      'AI symptom triage & diagnostics',
      'Telemedicine & virtual consultations',
      'HIPAA & GDPR compliant by design',
    ],
  },
];

export function SaaSProductsSection() {
  return (
    <section className="py-20 bg-muted/30 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Layers className="w-5 h-5 text-amber-500" />
            <span className="text-amber-500 font-semibold tracking-wider uppercase text-sm">
              Ortem-Built SaaS
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Our Own SaaS Products
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Beyond client work — these are production SaaS platforms we designed, built, and ship.
            Available as white-label solutions for your business.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => {
            const { Icon } = product;
            const linkHref = product.productSlug ?? product.externalUrl ?? '#';
            const isExternal = !product.productSlug;

            return (
              <TiltCard
                key={product.name}
                className="group glass-card-hover rounded-xl p-6 transition-all duration-300 flex flex-col border border-border/50 bg-card/50 backdrop-blur-sm relative"
              >
                {/* Ortem-Built badge */}
                <span className="absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 uppercase tracking-wider">
                  Ortem-Built
                </span>

                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${product.gradientClass} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <h3 className="font-semibold text-foreground text-lg leading-tight group-hover:text-primary transition-colors">
                      {product.name}
                    </h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium inline-block mt-1">
                      {product.category}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4 font-medium">
                  {product.tagline}
                </p>

                <ul className="space-y-1.5 mb-5 flex-1">
                  {product.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-2 mb-5">
                  {product.tech.map((t) => (
                    <span key={t} className="text-[10px] px-2 py-1 rounded-md bg-secondary text-secondary-foreground">
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-auto pt-4 border-t border-border/50 flex gap-2">
                  {isExternal ? (
                    <a
                      href={linkHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md"
                    >
                      Explore
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={linkHref}
                      className="flex-1 inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md"
                    >
                      Explore
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                  <button
                    onClick={openCalendlyPopup}
                    className="flex-1 inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg font-medium border border-border hover:bg-accent transition-all"
                  >
                    Get a Demo
                  </button>
                </div>
              </TiltCard>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/products/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View all products & white-label options
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
